import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/ja";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import { Check, Loader2, MessagesSquare } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SidebarSectionHeading } from "@/components/common/SidebarSectionHeading";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Switch } from "@/components/ui/switch";
import { useBlockedUsersList } from "@/hooks/useBlockedUsersList";
import { useInViewport } from "@/hooks/useInViewport";
import { usePersistentBooleanState } from "@/hooks/usePersistentBooleanState";
import { useTopicChannels } from "@/features/study-group-stream/hooks/useTopicChannels";
import { CATCH_UP_POSITIONING_EVENT } from "@/features/study-group-stream/hooks/useStreamActivityNavigation";
import {
  type CatchUpItem,
  STREAM_ACTIVITIES_READ_EVENT,
  streamReadApi,
} from "@/lib/humhub-api/streamRead";
import { useGlobalUserInfo } from "@/providers/AuthProvider";

dayjs.extend(relativeTime);
dayjs.extend(utc);

const CATCH_UP_REFRESH_INTERVAL_MS = 60_000;

interface CatchUpListProps {
  containerId?: string | number;
  height?: string;
  studyGroupId?: string | number;
  deferFetchMs?: number;
}

const getInitials = (displayName: string): string =>
  displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "U";

const formatRelativeActivityTime = (createdAt: string): string => {
  const localTime = dayjs.utc(createdAt).local();
  return localTime.isValid() ? localTime.fromNow() : createdAt;
};

const getActivityLabel = (
  item: CatchUpItem,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  const actorName =
    item.actor?.displayName ||
    t("common.unknownUser", {
      defaultValue: "Unknown user",
    });
  return item.activityType === "post"
    ? t("common.createdAPost", {
        actor: actorName,
        defaultValue: `${actorName} created a post`,
      })
    : item.activityType === "reply"
      ? t("common.replied", {
          actor: actorName,
          defaultValue: `${actorName} replied`,
        })
      : t("common.commented", {
          actor: actorName,
          defaultValue: `${actorName} commented`,
        });
};

export const CatchUpList: React.FC<CatchUpListProps> = ({
  height = "h-60",
  studyGroupId,
  deferFetchMs = 0,
}) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const currentUser = useGlobalUserInfo();
  const { "study-group-id": studyGroupIdFromParams } = useParams({
    strict: false,
  });
  const numericGroupId = Number(studyGroupId ?? studyGroupIdFromParams);
  const { data: topicChannels } = useTopicChannels(numericGroupId);
  const { value: includeComments, setPersistentValue: setIncludeComments } =
    usePersistentBooleanState(
      `stream-catch-up-include-comments:${numericGroupId}`,
      true,
    );
  const [dismissedIds, setDismissedIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [isFetchEnabled, setIsFetchEnabled] = useState(deferFetchMs <= 0);
  const [openingItemId, setOpeningItemId] = useState<number | null>(null);
  const openingItemIdRef = useRef<number | null>(null);
  const { ref: containerRef, isInViewport } = useInViewport<HTMLDivElement>({
    rootMargin: "240px",
  });
  const { data: blockedUsers = [] } = useBlockedUsersList();
  const blockedUserIds = useMemo(
    () => new Set(blockedUsers.map((id) => String(id))),
    [blockedUsers],
  );
  const channelNameById = useMemo(
    () =>
      new Map(
        (topicChannels?.results ?? []).flatMap((channel) =>
          channel.id != null && channel.name
            ? [[String(channel.id), channel.name] as const]
            : [],
        ),
      ),
    [topicChannels?.results],
  );

  useEffect(() => {
    dayjs.locale(i18n.language.startsWith("ja") ? "ja" : "en");
  }, [i18n.language]);

  useEffect(() => {
    const handlePositioning = (event: Event) => {
      const isPositioning = Boolean(
        (event as CustomEvent<{ isPositioning?: boolean }>).detail
          ?.isPositioning,
      );
      if (!isPositioning) {
        openingItemIdRef.current = null;
        setOpeningItemId(null);
      }
    };
    window.addEventListener(CATCH_UP_POSITIONING_EVENT, handlePositioning);
    return () =>
      window.removeEventListener(CATCH_UP_POSITIONING_EVENT, handlePositioning);
  }, []);

  useEffect(() => {
    void numericGroupId;
    setDismissedIds(new Set());
    setIsFetchEnabled(deferFetchMs <= 0);
  }, [deferFetchMs, numericGroupId]);

  useEffect(() => {
    if (!isInViewport || isFetchEnabled) return;
    const timer = window.setTimeout(
      () => setIsFetchEnabled(true),
      deferFetchMs,
    );
    return () => window.clearTimeout(timer);
  }, [deferFetchMs, isFetchEnabled, isInViewport]);

  const {
    data: catchUpItems = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["stream-catch-up", numericGroupId, includeComments],
    queryFn: () =>
      streamReadApi.getCatchUp(numericGroupId, {
        includeComments,
        limit: 20,
      }),
    enabled:
      isInViewport &&
      isFetchEnabled &&
      Number.isSafeInteger(numericGroupId) &&
      numericGroupId > 0,
    refetchInterval:
      isInViewport && isFetchEnabled ? CATCH_UP_REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const visibleItems = useMemo(
    () =>
      catchUpItems.filter(
        (item) =>
          !dismissedIds.has(item.id) &&
          !blockedUserIds.has(String(item.createdBy)) &&
          item.createdBy !== currentUser?.id,
      ),
    [blockedUserIds, catchUpItems, currentUser?.id, dismissedIds],
  );

  const markRead = useCallback(
    (item: CatchUpItem) => {
      setDismissedIds((current) => new Set(current).add(item.id));
      for (const includeCommentsValue of [false, true]) {
        queryClient.setQueryData<CatchUpItem[]>(
          ["stream-catch-up", numericGroupId, includeCommentsValue],
          (current = []) =>
            current.filter((currentItem) => currentItem.id !== item.id),
        );
      }
      queryClient.setQueryData<CatchUpItem[]>(
        ["stream-unread-activities", numericGroupId],
        (current = []) =>
          current.filter((currentItem) => currentItem.id !== item.id),
      );
      if (Number.isSafeInteger(numericGroupId) && numericGroupId > 0) {
        void streamReadApi
          .markRead(numericGroupId, [item.id], "catch_up_check")
          .then(() => {
            window.dispatchEvent(
              new CustomEvent(STREAM_ACTIVITIES_READ_EVENT, {
                detail: {
                  activityIds: [item.id],
                  activityKeys: [`${item.activityType}:${item.activityId}`],
                  postContentIds:
                    item.activityType === "post" ? [item.postContentId] : [],
                },
              }),
            );
          })
          .catch(() => {
            setDismissedIds((current) => {
              const restored = new Set(current);
              restored.delete(item.id);
              return restored;
            });
            void queryClient.invalidateQueries({
              queryKey: ["stream-catch-up", numericGroupId],
            });
            void queryClient.invalidateQueries({
              queryKey: ["stream-unread-activities", numericGroupId],
            });
          });
      }
    },
    [numericGroupId, queryClient],
  );

  const openItem = useCallback(
    (item: CatchUpItem) => {
      if (openingItemIdRef.current !== null) return;
      openingItemIdRef.current = item.id;
      setOpeningItemId(item.id);
      const search = {
        catch_up_id: item.id,
        activity_type: item.activityType,
        activity_id: item.activityId,
        post_content_id: item.postContentId,
        parent_comment_id: item.parentCommentId ?? undefined,
        activity_created_at: item.createdAt,
      };
      if (item.channelId) {
        navigate({
          to: "/study-group/$study-group-id/stream/channel/$channel-id",
          params: {
            "study-group-id": String(numericGroupId),
            "channel-id": String(item.channelId),
          },
          search,
        });
        return;
      }
      navigate({
        to: "/study-group/$study-group-id/stream",
        params: { "study-group-id": String(numericGroupId) },
        search,
      });
    },
    [navigate, numericGroupId],
  );

  const heading = (
    <div className="flex min-w-0 items-center justify-between gap-2 max-[1399px]:gap-1">
      <SidebarSectionHeading icon={<MessagesSquare className="size-4" />}>
        {t("common.catchUp")}
      </SidebarSectionHeading>
      <div className="mr-0.5 flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[11px] text-rw-dark-gray max-[1399px]:gap-1 max-[1399px]:text-[10px]">
        <span>
          {t("common.includeComments", { defaultValue: "Include comments" })}
        </span>
        <Switch
          size="sm"
          checked={includeComments}
          onCheckedChange={(checked) => {
            setIncludeComments(checked);
            setDismissedIds(new Set());
          }}
          aria-label={t("common.includeComments", {
            defaultValue: "Include comments",
          })}
          className="shrink-0 data-[state=checked]:bg-rw-main data-[state=unchecked]:bg-slate-300"
        />
      </div>
    </div>
  );

  return (
    <section ref={containerRef} className="flex size-full min-h-0 flex-col">
      {heading}
      <hr className="my-3 border-t border-slate-200 [@media(max-height:700px)]:my-2" />
      <div className={`${height} min-h-0 overflow-y-auto`}>
        {isLoading || !isFetchEnabled ? (
          <div className="space-y-1" aria-busy="true">
            <span className="sr-only">
              {t("common.loading", { defaultValue: "Loading" })}
            </span>
            {["skeleton-1", "skeleton-2", "skeleton-3"].map((key) => (
              <div
                key={key}
                aria-hidden="true"
                className="flex animate-pulse items-start gap-2 py-2"
              >
                <div className="size-8 shrink-0 rounded-full bg-slate-200" />
                <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-0.5">
                  <div className="h-3 w-11/12 rounded-full bg-slate-200" />
                  <div className="h-3 w-3/5 rounded-full bg-slate-200" />
                  <div className="h-2 w-1/4 rounded-full bg-slate-100" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-red-500">
              {t("common.failedToLoadActivities")}
            </p>
            <button
              type="button"
              className="rounded-full border border-[rgba(10,80,94,0.35)] px-3 py-1 text-xs font-semibold text-[rgba(10,80,94,1)] transition-colors hover:bg-[rgba(10,80,94,0.06)]"
              onClick={() => void refetch()}
            >
              {t("common.retry", { defaultValue: "Retry" })}
            </button>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="flex items-center gap-2 py-2 text-sm text-[rgba(86,90,91,1)] [@media(max-height:700px)]:py-1.5 [@media(max-height:700px)]:text-[13px]">
            <span className="flex size-6 items-center justify-center rounded-full bg-[rgba(10,80,94,0.1)] text-[rgba(10,80,94,1)]">
              <Check className="size-3.5" />
            </span>
            {t("common.allCaughtUp", { defaultValue: "You're all caught up" })}
          </div>
        ) : (
          <div className="space-y-1 pr-2">
            {visibleItems.map((item) => {
              const label = getActivityLabel(item, t);
              const channelLabel = item.channelId
                ? `# ${
                    channelNameById.get(String(item.channelId)) ??
                    t("common.channel", { defaultValue: "Channel" })
                  }`
                : t("common.home", { defaultValue: "Home" });
              return (
                <div
                  key={item.id}
                  className="group/catch-up focus-within:ring-rw-main/30 relative flex w-full items-start gap-2 rounded-lg py-2 pl-0 pr-9 transition-colors focus-within:outline-none focus-within:ring-2 hover:bg-gray-50"
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-2 rounded-lg text-left focus-visible:outline-none"
                    onClick={() => openItem(item)}
                    disabled={openingItemId !== null}
                    aria-busy={openingItemId === item.id || undefined}
                  >
                    <Avatar className="size-8 shrink-0 rounded-full">
                      <AvatarImage
                        src={item.actor?.imageUrl || ""}
                        alt={item.actor?.displayName || ""}
                        className="rounded-full"
                      />
                      <AvatarFallback className="rounded-full">
                        {getInitials(item.actor?.displayName || "")}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span
                        className="line-clamp-2 break-words text-[13px] font-normal text-[rgba(86,90,91,1)]"
                        title={`${label}${item.summary ? ` “${item.summary}”` : ""}`}
                      >
                        {label}
                        {item.summary ? ` “${item.summary}”` : ""}
                      </span>
                      <span className="flex items-center gap-1 text-[10px] font-medium text-[rgba(157,165,171,1)]">
                        <span
                          className="max-w-28 truncate"
                          title={channelLabel}
                        >
                          {channelLabel}
                        </span>
                        <span aria-hidden="true">·</span>
                        <time>
                          {formatRelativeActivityTime(item.createdAt)}
                        </time>
                      </span>
                    </span>
                  </button>
                  {openingItemId === item.id ? (
                    <Loader2
                      className="absolute right-2.5 top-4 size-4 animate-spin text-rw-main"
                      aria-label={t("common.loading", {
                        defaultValue: "Loading",
                      })}
                    />
                  ) : (
                    <span
                      className={`absolute right-3 top-5 size-2 rounded-full transition-opacity group-hover/catch-up:opacity-0 ${
                        item.isMention ? "bg-red-500" : "bg-sky-500"
                      }`}
                      role="img"
                      aria-label={
                        item.isMention
                          ? t("common.mention", { defaultValue: "Mention" })
                          : t("common.unread", { defaultValue: "Unread" })
                      }
                    />
                  )}
                  <button
                    type="button"
                    className="absolute right-0 top-2 flex size-7 items-center justify-center rounded-full bg-[rgba(10,80,94,1)] text-white opacity-0 transition-opacity hover:bg-[rgba(7,61,72,1)] focus-visible:opacity-100 group-hover/catch-up:opacity-100"
                    onClick={() => markRead(item)}
                    aria-label={t("common.markAsRead", {
                      defaultValue: "Mark as read",
                    })}
                  >
                    <Check className="size-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
